/**
 * Browser Fingerprinting — supplements IP-based rate limiting
 *
 * Collects stable, non-PII browser signals and hashes them into a short
 * fingerprint token. Sent as `X-Client-Fingerprint` on every search request.
 *
 * Signals used (all available without user permission):
 *   - Canvas pixel hash (GPU renderer signature)
 *   - Screen resolution + color depth
 *   - Timezone
 *   - Language preference
 *   - CPU core count + device memory tier
 *   - Platform string
 *   - WebGL renderer string (GPU model)
 */

async function sha256Short(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .slice(0, 12) // 96-bit prefix — enough to be unique, not full PII
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function canvasSignal(): string {
  try {
    const c = document.createElement('canvas');
    c.width = 200;
    c.height = 40;
    const ctx = c.getContext('2d');
    if (!ctx) return 'no-canvas';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 200, 40);
    ctx.fillStyle = '#069';
    ctx.font = '14px Arial';
    ctx.fillText('bank-eldam-fp', 10, 25);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('bank-eldam-fp', 12, 27);
    return c.toDataURL().slice(-40); // tail of base64 — GPU-specific
  } catch {
    return 'canvas-blocked';
  }
}

function webGLSignal(): string {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl') ?? c.getContext('experimental-webgl') as WebGLRenderingContext | null;
    if (!gl) return 'no-webgl';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return 'no-ext';
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
  } catch {
    return 'webgl-blocked';
  }
}

function deviceMemoryTier(): string {
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (!mem) return 'unknown';
  if (mem <= 2) return 'low';
  if (mem <= 4) return 'mid';
  return 'high';
}

export async function getBrowserFingerprint(): Promise<string> {
  const signals = [
    canvasSignal(),
    webGLSignal(),
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    String(navigator.hardwareConcurrency ?? 0),
    deviceMemoryTier(),
    navigator.platform,
  ].join('|');

  return sha256Short(signals);
}
